import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail, PlusCircle, RefreshCcw, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog from '../../../components/ui/loeschen-mit-grund-dialog';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import MessageWizardModal, { type MessageRecipient } from '../../../components/communications/MessageWizardModal';
import { useStandort } from '../../../components/layouts/use-standort';
import { usePermissions } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import { api } from '../../../lib/api';
import { personListMeta } from '../../../lib/api/core';
import { personTypesApi, type PersonType } from '../../../lib/person-types';
import { personListsApi, type PersonListItem, type PersonListMember, type PersonListVisibility } from '../../../lib/person-lists';
import { TYPE_ALIASES, usePersonFilterSelection, type PersonFilterSort } from '../../../hooks/usePersonFilterSelection';

interface Person {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: 'student' | 'teacher' | 'customer' | 'both';
  status: 'active' | 'inactive' | 'pending';
  joinDate: string;
  wpUserId?: string;
  parentId?: string;
}

const PAGE_SIZE = 20;
const TYPE_FALLBACK_LABELS: Record<string, string> = {
  customer: 'Kunde',
  teacher: 'Lehrer',
  student: 'Schüler',
  subprofile: 'Schüler',
  both: 'Kunde & Lehrer',
};
const BACKEND_PERSON_PAGE_SIZE = 200;
type ListScope = 'mine' | 'public' | 'all';

const toMemberKey = (wpUserId: number, subIndex?: number | null) => `${wpUserId}:${subIndex ?? ''}`;
const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const PersonListsPage = () => {
  const { selectedStandortId } = useStandort();
  const { can } = usePermissions();
  // „Alle Listen“ nur mit unternehmensweitem Scope auf Personenlisten.
  const canSeeAllLists = can(P.personenlisten.personenlisten_ansehen, { minScope: 'company' });

  const [isLoading, setIsLoading] = useState(true);
  const [ohneStandort, setOhneStandort] = useState(0);
  const [people, setPeople] = useState<Person[]>([]);
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);
  const [lists, setLists] = useState<PersonListItem[]>([]);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [listeLoeschenOffen, setListeLoeschenOffen] = useState(false);
  const [members, setMembers] = useState<PersonListMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  // Zu welcher Liste `members` gehört. Die automatische Auswahl darf erst
  // laufen, wenn die Mitglieder der gerade gewählten Liste da sind.
  const [membersListId, setMembersListId] = useState<number | null>(null);
  const [scope, setScope] = useState<ListScope>('mine');

  const [searchTerm, setSearchTerm] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState<PersonFilterSort>('name_asc');
  const [page, setPage] = useState(0);
  const [onlyListMembers, setOnlyListMembers] = useState(false);

  const [newListName, setNewListName] = useState('');
  const [newListVisibility, setNewListVisibility] = useState<PersonListVisibility>('private');
  const [newListDescription, setNewListDescription] = useState('');
  const [savingList, setSavingList] = useState(false);

  const [messageOpen, setMessageOpen] = useState(false);
  const [messageRecipients, setMessageRecipients] = useState<MessageRecipient[]>([]);
  const [addToListDialogOpen, setAddToListDialogOpen] = useState(false);
  const [addToListTargetId, setAddToListTargetId] = useState<string>('');
  const [autoSelectMembersPending, setAutoSelectMembersPending] = useState(false);
  const [autoSelectedCount, setAutoSelectedCount] = useState<number | null>(null);

  const selectedList = useMemo(
    () => lists.find((list) => list.id === selectedListId) || null,
    [lists, selectedListId],
  );

  const fetchAllPersonsForStandort = useCallback(async (standortId: string): Promise<Person[]> => {
    const all: Person[] = [];
    let offset = 0;

    for (;;) {
      const page = await api.get<Person[]>(
        `/api/personen?standort_id=${standortId}&limit=${BACKEND_PERSON_PAGE_SIZE}&offset=${offset}`,
      );
      all.push(...(page || []));
      // Wer zu keinem Standort gehört, fällt aus dem Filter. Für eine Massenmail
      // wäre ein stilles Fehlen die schlechteste Variante — also sagen wir es.
      setOhneStandort(personListMeta(page).withoutStandort);

      if (!page || page.length < BACKEND_PERSON_PAGE_SIZE) break;
      offset += BACKEND_PERSON_PAGE_SIZE;
    }

    return all;
  }, []);

  const loadBaseData = useCallback(async () => {
    if (!selectedStandortId) return;
    setIsLoading(true);
    try {
      const [loadedTypes, loadedPeople] = await Promise.all([
        personTypesApi.list(),
        fetchAllPersonsForStandort(selectedStandortId),
      ]);
      setPersonTypes(loadedTypes.filter((type) => type.aktiv));
      setPeople(loadedPeople);
    } catch {
      toast.error('Personen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllPersonsForStandort, selectedStandortId]);

  const loadLists = useCallback(async (nextScope: ListScope = scope) => {
    try {
      const standortId = selectedStandortId ? Number(selectedStandortId) : undefined;
      const resolvedScope: ListScope =
        nextScope === 'all' && !canSeeAllLists ? 'mine' : nextScope;
      const loaded = await personListsApi.list({
        scope: resolvedScope,
        standortId,
      });
      setLists(loaded);
      setSelectedListId((prev) => (prev && loaded.some((list) => list.id === prev) ? prev : null));
    } catch {
      toast.error('Listen konnten nicht geladen werden');
    }
  }, [canSeeAllLists, scope, selectedStandortId]);

  const loadMembers = useCallback(async (listId: number | null) => {
    if (!listId) {
      setMembers([]);
      setMembersListId(null);
      return;
    }
    setMembersLoading(true);
    try {
      const loaded = await personListsApi.getMembers(listId);
      setMembers(loaded);
      setMembersListId(listId);
    } catch {
      toast.error('Mitglieder konnten nicht geladen werden');
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    void loadLists(scope);
  }, [loadLists, scope]);

  useEffect(() => {
    void loadMembers(selectedListId);
  }, [loadMembers, selectedListId]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, activeType, statusFilter, sortBy, onlyListMembers, selectedListId]);

  const memberIdSet = useMemo(() => {
    const set = new Set<string>();
    for (const member of members) {
      set.add(toMemberKey(Number(member.wp_user_id), member.sub_index ?? null));
    }
    return set;
  }, [members]);

  const allowedIds = useMemo(() => {
    if (!onlyListMembers || !selectedListId) return undefined;
    const ids = new Set<string>();
    for (const person of people) {
      const wpUserId = Number.parseInt(String(person.wpUserId || person.id || ''), 10);
      if (!wpUserId || Number.isNaN(wpUserId)) continue;
      if (memberIdSet.has(toMemberKey(wpUserId, null))) {
        ids.add(person.id);
      }
    }
    return ids;
  }, [onlyListMembers, selectedListId, people, memberIdSet]);

  const {
    selectedIds,
    filteredPeople,
    pagedPeople,
    totalFiltered,
    pageStartIdx,
    pageEndIdx,
    totalPages,
    toggleSelected,
    clearSelection,
    replaceSelection,
    togglePageSelection,
    pageSelectionState,
  } = usePersonFilterSelection<Person>({
    people,
    searchTerm,
    activeType,
    statusFilter,
    sortBy,
    page,
    pageSize: PAGE_SIZE,
    allowedIds,
  });

  const selectedPeople = useMemo(() => {
    if (selectedIds.size === 0) return [];
    const map = new Map(people.map((person) => [person.id, person]));
    return Array.from(selectedIds)
      .map((id) => map.get(id))
      .filter((person): person is Person => !!person);
  }, [selectedIds, people]);

  const memberPersonIds = useMemo(() => {
    const ids: string[] = [];
    if (!selectedListId) return ids;

    const memberWpUserIds = new Set<number>();
    for (const member of members) {
      memberWpUserIds.add(Number(member.wp_user_id));
    }

    for (const person of people) {
      const wpUserId = Number.parseInt(String(person.wpUserId || person.id || ''), 10);
      if (!wpUserId || Number.isNaN(wpUserId)) continue;
      if (memberWpUserIds.has(wpUserId)) {
        ids.push(person.id);
      }
    }
    return ids;
  }, [members, people, selectedListId]);

  useEffect(() => {
    if (!autoSelectMembersPending) return;
    if (!selectedListId) return;
    if (membersLoading || membersListId !== selectedListId) return;
    replaceSelection(memberPersonIds);
    setAutoSelectedCount(memberPersonIds.length);
    setAutoSelectMembersPending(false);
  }, [autoSelectMembersPending, selectedListId, membersLoading, membersListId, memberPersonIds, replaceSelection]);

  useEffect(() => {
    if (!selectedListId) {
      setAutoSelectedCount(null);
    }
  }, [selectedListId]);

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (name === '') {
      toast.error('Bitte einen Listennamen eingeben');
      return;
    }
    setSavingList(true);
    try {
      const created = await personListsApi.create({
        name,
        description: newListDescription.trim() || undefined,
        visibility: newListVisibility,
        standort_id: selectedStandortId ? Number(selectedStandortId) : null,
      });
      setNewListName('');
      setNewListDescription('');
      toast.success('Liste wurde erstellt');
      await loadLists(scope);
      setSelectedListId(created.id);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Liste konnte nicht erstellt werden'));
    } finally {
      setSavingList(false);
    }
  };

  const handleUpdateList = async () => {
    if (!selectedList) return;
    const nextName = globalThis.prompt('Neuer Listenname', selectedList.name);
    if (!nextName) return;
    try {
      await personListsApi.update(selectedList.id, { name: nextName.trim() });
      toast.success('Liste wurde aktualisiert');
      await loadLists(scope);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Liste konnte nicht aktualisiert werden'));
    }
  };

  const handleToggleVisibility = async () => {
    if (!selectedList) return;
    const nextVisibility: PersonListVisibility = selectedList.visibility === 'private' ? 'public' : 'private';
    try {
      await personListsApi.update(selectedList.id, { visibility: nextVisibility });
      toast.success('Sichtbarkeit wurde geändert');
      await loadLists(scope);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Sichtbarkeit konnte nicht geändert werden'));
    }
  };

  const handleDeleteList = async (grund: string) => {
    if (!selectedList) return;
    await personListsApi.delete(selectedList.id, grund);
    toast.success('Liste wurde gelöscht');
    setListeLoeschenOffen(false);
    setSelectedListId(null);
    setMembers([]);
    await loadLists(scope);
  };

  const editableLists = useMemo(() => lists.filter((list) => list.can_edit), [lists]);

  const openAddToListDialog = () => {
    if (selectedPeople.length === 0) {
      toast.error('Keine Personen ausgewählt');
      return;
    }
    if (editableLists.length === 0) {
      toast.error('Keine bearbeitbare Zielliste verfügbar');
      return;
    }
    const defaultTarget = selectedList?.can_edit ? String(selectedList.id) : String(editableLists[0].id);
    setAddToListTargetId(defaultTarget);
    setAddToListDialogOpen(true);
  };

  const handleConfirmAddToList = async () => {
    const targetListId = Number.parseInt(addToListTargetId, 10);
    if (!targetListId || Number.isNaN(targetListId)) {
      toast.error('Bitte eine Zielliste auswählen');
      return;
    }

    const payload = selectedPeople
      .map((person) => Number.parseInt(String(person.wpUserId || person.id || ''), 10))
      .filter((wpUserId) => wpUserId > 0 && !Number.isNaN(wpUserId))
      .map((wpUserId) => ({ wp_user_id: wpUserId }));

    if (payload.length === 0) {
      toast.error('Auswahl enthält keine gültigen Personen');
      return;
    }

    try {
      await personListsApi.addMembers(targetListId, payload);
      toast.success(`${payload.length} Person(en) zur Liste hinzugefügt`);
      setAddToListDialogOpen(false);
      clearSelection();
      if (selectedListId === targetListId) {
        await loadMembers(targetListId);
      }
      await loadLists(scope);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Personen konnten nicht hinzugefügt werden'));
    }
  };

  const handleRemoveSelectionFromList = async () => {
    if (!selectedList) {
      toast.error('Bitte zuerst eine Liste auswählen');
      return;
    }
    if (selectedPeople.length === 0) {
      toast.error('Keine Personen ausgewählt');
      return;
    }

    const memberIdsToRemove = new Set<number>();
    const memberByKey = new Map<string, PersonListMember>();
    for (const member of members) {
      memberByKey.set(toMemberKey(Number(member.wp_user_id), member.sub_index ?? null), member);
    }
    for (const person of selectedPeople) {
      const wpUserId = Number.parseInt(String(person.wpUserId || person.id || ''), 10);
      if (!wpUserId || Number.isNaN(wpUserId)) continue;
      const member = memberByKey.get(toMemberKey(wpUserId, null));
      if (member) memberIdsToRemove.add(member.id);
    }

    if (memberIdsToRemove.size === 0) {
      toast.error('Keine ausgewählten Personen sind in der Liste');
      return;
    }

    try {
      await Promise.all(Array.from(memberIdsToRemove).map((memberId) => personListsApi.removeMember(selectedList.id, memberId)));
      toast.success(`${memberIdsToRemove.size} Person(en) aus Liste entfernt`);
      clearSelection();
      await loadMembers(selectedList.id);
      await loadLists(scope);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Personen konnten nicht entfernt werden'));
    }
  };

  const handleOpenMassMail = () => {
    if (selectedPeople.length === 0) {
      toast.error('Bitte zuerst Personen auswählen');
      return;
    }
    const recipients: MessageRecipient[] = [];
    for (const person of selectedPeople) {
      const wpUserId = Number.parseInt(String(person.wpUserId || person.id || ''), 10);
      if (!wpUserId || Number.isNaN(wpUserId)) continue;
      recipients.push({
        id: `person:${wpUserId}`,
        type: 'person',
        wpUserId,
        name: person.name,
        email: person.email || null,
        phone: person.phone || null,
      });
    }

    if (recipients.length === 0) {
      toast.error('Auswahl enthält keine gültigen Empfänger');
      return;
    }
    setMessageRecipients(recipients);
    setMessageOpen(true);
  };

  // Die Personen tragen `customer`/`teacher`, die Stammdaten `kunde`/`lehrer`.
  const typeLabel = (typeCode: string) =>
    personTypes.find((type) => type.code === typeCode || TYPE_ALIASES[type.code] === typeCode)?.name
    || TYPE_FALLBACK_LABELS[typeCode]
    || typeCode;

  const visibilityLabel = selectedList?.visibility === 'private' ? 'privat' : 'public';

  let tableContent: React.ReactNode;
  if (isLoading) {
    tableContent = <div className="rounded border p-10 text-center text-muted-foreground">Lade Personen...</div>;
  } else if (filteredPeople.length === 0) {
    tableContent = (
      <div className="rounded border p-10 text-center">
        <Users className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Keine Personen für die aktuelle Auswahl gefunden.</p>
      </div>
    );
  } else {
    tableContent = (
      <div className="space-y-3">
        <Table density="compact">
          <TableHeader>
            <TableRow>
              <TableHead density="compact" className="w-[40px]">
                <Checkbox
                  checked={pageSelectionState.all}
                  onCheckedChange={(value) => togglePageSelection(Boolean(value))}
                />
              </TableHead>
              <TableHead density="compact">Name</TableHead>
              <TableHead density="compact">E-Mail</TableHead>
              <TableHead density="compact">Typ</TableHead>
              <TableHead density="compact">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedPeople.map((person) => (
              <TableRow key={person.id}>
                <TableCell density="compact">
                  <Checkbox
                    checked={selectedIds.has(person.id)}
                    onCheckedChange={(value) => toggleSelected(person.id, Boolean(value))}
                  />
                </TableCell>
                <TableCell density="compact" className="font-medium">{person.name}</TableCell>
                <TableCell density="compact">{person.email || '—'}</TableCell>
                <TableCell density="compact">{typeLabel(person.type)}</TableCell>
                <TableCell density="compact">{person.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Zeige {totalFiltered === 0 ? 0 : pageStartIdx + 1}-{pageEndIdx} von {totalFiltered} Personen
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={page <= 0}>
              Zurück
            </Button>
            <span className="text-sm text-muted-foreground">
              Seite {page + 1} / {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
              disabled={page + 1 >= totalPages}
            >
              Weiter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Personenlisten & Massenmail</h1>
          <p className="text-muted-foreground">Personen suchen, in Listen organisieren und gesammelt anschreiben.</p>
          {ohneStandort > 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {ohneStandort === 1
                ? '1 Person ohne Standortzuordnung ist hier nicht dabei.'
                : `${ohneStandort} Personen ohne Standortzuordnung sind hier nicht dabei.`}
            </p>
          ) : null}
        </div>
        <Button variant="outline" onClick={() => { void loadBaseData(); void loadLists(scope); }} disabled={isLoading}>
          <RefreshCcw className="mr-2 h-4 w-4" />
          Aktualisieren
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <aside className="space-y-4 rounded-lg border bg-card p-4">
          <div className="space-y-2">
            <h2 className="font-medium">Listen</h2>
            <Select value={scope} onValueChange={(value) => setScope(value as 'mine' | 'public' | 'all')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mine">Meine Listen</SelectItem>
                <SelectItem value="public">Public Listen</SelectItem>
                {canSeeAllLists ? <SelectItem value="all">Alle Listen (Admin)</SelectItem> : null}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            {lists.length === 0 ? (
              <div className="rounded border border-dashed p-3 text-sm text-muted-foreground">
                Keine Listen vorhanden.
              </div>
            ) : (
              lists.map((list) => (
                <button
                  key={list.id}
                  type="button"
                  onClick={() => {
                    setSelectedListId(list.id);
                    setOnlyListMembers(true);
                    setAutoSelectMembersPending(true);
                    setPage(0);
                  }}
                  className={`w-full rounded border px-3 py-2 text-left transition ${
                    selectedListId === list.id ? 'border-primary bg-primary/10' : 'hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium">{list.name}</span>
                    <Badge variant={list.visibility === 'public' ? 'info' : 'secondary'}>
                      {list.visibility === 'public' ? 'public' : 'privat'}
                    </Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {list.member_count ?? 0} Personen
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="space-y-2 border-t pt-3">
            <h3 className="text-sm font-medium">Neue Liste</h3>
            <Input placeholder="Listenname" value={newListName} onChange={(e) => setNewListName(e.target.value)} />
            <Input placeholder="Beschreibung (optional)" value={newListDescription} onChange={(e) => setNewListDescription(e.target.value)} />
            <Select value={newListVisibility} onValueChange={(value) => setNewListVisibility(value as PersonListVisibility)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Privat</SelectItem>
                <SelectItem value="public">Public</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreateList} disabled={savingList} className="w-full">
              <PlusCircle className="mr-2 h-4 w-4" />
              Liste erstellen
            </Button>
          </div>
        </aside>

        <section className="space-y-4 rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Person suchen..."
              className="max-w-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Select value={activeType} onValueChange={setActiveType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Typ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Typen</SelectItem>
                {personTypes.map((type) => (
                  <SelectItem key={type.code} value={type.code}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="inactive">Inaktiv</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(value) => setSortBy(value as PersonFilterSort)}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Sortierung" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Name A-Z</SelectItem>
                <SelectItem value="name_desc">Name Z-A</SelectItem>
                <SelectItem value="newest_join">Neueste zuerst</SelectItem>
                <SelectItem value="oldest_join">Älteste zuerst</SelectItem>
              </SelectContent>
            </Select>
            <label className="ml-2 flex items-center gap-2 text-sm">
              <Checkbox
                checked={onlyListMembers}
                onCheckedChange={(value) => setOnlyListMembers(Boolean(value))}
                disabled={!selectedListId || membersLoading}
              />
              Nur Listenmitglieder
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded border bg-muted/30 p-2">
            <Badge variant="secondary">{selectedIds.size} ausgewählt</Badge>
            {selectedListId && autoSelectedCount !== null ? (
              <Badge variant="info">{autoSelectedCount} Mitglieder automatisch ausgewählt</Badge>
            ) : null}
            <Button size="sm" onClick={openAddToListDialog} disabled={selectedIds.size === 0}>
              Zur Liste hinzufügen
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRemoveSelectionFromList}
              disabled={!selectedList || selectedIds.size === 0}
            >
              Aus Liste entfernen
            </Button>
            <Button size="sm" onClick={handleOpenMassMail} disabled={selectedIds.size === 0}>
              <Mail className="mr-2 h-4 w-4" />
              Massenmail schreiben
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} disabled={selectedIds.size === 0}>
              Auswahl leeren
            </Button>
            {selectedList?.can_edit ? (
              <>
                <Button size="sm" variant="outline" onClick={handleUpdateList}>
                  Liste umbenennen
                </Button>
                <Button size="sm" variant="outline" onClick={handleToggleVisibility}>
                  Sichtbarkeit: {visibilityLabel}
                </Button>
                {can(P.personenlisten.personenliste_loeschen) ? (
                  <Button size="sm" variant="destructive" onClick={() => setListeLoeschenOffen(true)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Liste löschen
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>

          {tableContent}
        </section>
      </div>

      <MessageWizardModal
        open={messageOpen}
        onOpenChange={setMessageOpen}
        standortId={selectedStandortId ? Number(selectedStandortId) : 0}
        title="Massenmail an ausgewählte Personen"
        zweckWaehlbar
        recipients={messageRecipients}
        initialChannel="email"
        templateKeys={{
          email: 'communications.manual.person',
          sms: 'communications.manual.person',
        }}
      />

      <Dialog open={addToListDialogOpen} onOpenChange={setAddToListDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Zur Liste hinzufügen</DialogTitle>
            <DialogDescription>
              Wähle die Zielliste für {selectedPeople.length} ausgewählte Person(en).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Select value={addToListTargetId} onValueChange={setAddToListTargetId}>
              <SelectTrigger>
                <SelectValue placeholder="Zielliste wählen" />
              </SelectTrigger>
              <SelectContent>
                {editableLists.map((list) => (
                  <SelectItem key={list.id} value={String(list.id)}>
                    {list.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddToListDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleConfirmAddToList}>Hinzufügen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LoeschenMitGrundDialog
        offen={listeLoeschenOffen}
        titel={`Liste „${selectedList?.name ?? ''}“ löschen?`}
        onAbbrechen={() => setListeLoeschenOffen(false)}
        onLoeschen={handleDeleteList}
      />
    </div>
  );
};

export default PersonListsPage;
